/**
 * variant-gallery.js
 *
 * Duvin: on variant change, re-fetches this product section's own
 * server-rendered HTML for the newly selected variant (Shopify's Section
 * Rendering API) and swaps the main image slider + thumbnail strip into
 * the live page. This re-uses the exact Liquid gallery logic in
 * snippets/product.liquid (which filters by the variant's
 * custom.variant_imagery metafield, falling back to the full product
 * media pool when that variant has no gallery data yet) instead of
 * duplicating that logic client-side.
 *
 * Hooks into the theme's existing "theme:variant:change" custom event
 * (dispatched by product-form in theme.js), so no changes to the
 * existing variant-selection logic are needed.
 *
 * Fails silently on any error: worst case, the gallery simply doesn't
 * refresh and the page behaves as it did before this change (the
 * featured image still swaps via the theme's existing
 * variant_image_scroll behavior).
 */
(function () {
  'use strict';

  var CONTAINER_SELECTOR = '[data-section-type="product"]';
  var MEDIA_LIST_SELECTOR = '[data-product-media-list]';
  var THUMBS_SELECTOR = '.product__thumbs__holder';
  var IMAGES_TAG = 'product-images';

  // Avoid firing overlapping fetches if variants are clicked in quick succession
  var pendingRequestId = 0;

  function buildSectionUrl(container, variantId) {
    var handle = container.getAttribute('data-product-handle');
    var sectionId = container.getAttribute('data-section-id');
    if (!handle || !sectionId) return null;

    return (
      '/products/' +
      handle +
      '?variant=' +
      encodeURIComponent(variantId) +
      '&section_id=' +
      encodeURIComponent(sectionId)
    );
  }

  function swapGallery(container, variantId) {
    var url = buildSectionUrl(container, variantId);
    if (!url) return;

    var requestId = ++pendingRequestId;

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('Section fetch failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        // a newer variant change happened while this fetch was in flight
        if (requestId !== pendingRequestId) return;

        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        var freshImages = doc.querySelector(IMAGES_TAG);
        var liveImages = container.querySelector(IMAGES_TAG);
        if (!freshImages || !liveImages) return;

        var freshList = freshImages.querySelector(MEDIA_LIST_SELECTOR);
        var liveList = liveImages.querySelector(MEDIA_LIST_SELECTOR);
        if (freshList && liveList) {
          liveList.innerHTML = freshList.innerHTML;
        }

        var freshThumbs = freshImages.querySelector(THUMBS_SELECTOR);
        var liveThumbs = liveImages.querySelector(THUMBS_SELECTOR);
        if (freshThumbs && liveThumbs) {
          liveThumbs.innerHTML = freshThumbs.innerHTML;
        }

        var freshActiveMedia = freshImages.getAttribute('data-active-media');
        if (freshActiveMedia) {
          liveImages.setAttribute('data-active-media', freshActiveMedia);
        }

        // Let any slider/zoom/thumbs scripts that need to re-initialize
        // after a DOM swap know the gallery just changed.
        liveImages.dispatchEvent(
          new CustomEvent('theme:media:updated', { bubbles: true, detail: { variantId: variantId } })
        );
      })
      .catch(function (err) {
        // Silent failure by design - see file header.
        if (window.console && window.console.warn) {
          console.warn('[variant-gallery] could not refresh gallery for variant', variantId, err);
        }
      });
  }

  document.addEventListener('theme:variant:change', function (event) {
    var container = event.target && event.target.closest ? event.target.closest(CONTAINER_SELECTOR) : null;
    if (!container) return;

    var variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;

    swapGallery(container, variant.id);
  });
})();
